/**
 * Member Service - API methods for organization member management
 */

import apiClient from './apiClient';

// ============================================================================
// Types
// ============================================================================

export interface HierarchyMember {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  roles: { id: string; name: string }[];
  supervisorId: string | null;
  supervisorName: string | null;
  supervisees: { id: string; firstName: string; lastName: string }[];
}

export interface HierarchyResponse {
  members: HierarchyMember[];
  rootCount: number;
  unassignedCount: number;
}

export interface SeatCountResponse {
  used: number;
  total: number;
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * Get supervision hierarchy for the organization
 */
export const getHierarchy = async (): Promise<HierarchyResponse> => {
  const response = await apiClient.get<{ success: boolean; data: HierarchyResponse }>(
    '/api/v1/members/hierarchy',
  );
  return response.data.data;
};

/**
 * Get seat count for the organization
 */
export const getSeatCount = async (): Promise<SeatCountResponse> => {
  const response = await apiClient.get<{ success: boolean; data: SeatCountResponse }>(
    '/api/v1/members/seat-count',
  );
  return response.data.data;
};

/**
 * Update a member's supervisor
 */
export const updateSupervisor = async (
  memberId: string,
  supervisorId: string | null,
): Promise<void> => {
  await apiClient.patch(`/api/v1/members/${memberId}`, { supervisorId });
};

// ============================================================================
// Member Edit / Departure Types
// ============================================================================

export interface UpdateMemberPayload {
  roleIds?: string[];
  groupIds?: string[];
  supervisorId?: string | null;
  primaryCenterId?: string | null;
}

export interface AssignmentGroup {
  count: number;
  items: unknown[];
}

export interface ActiveAssignments {
  activeWorkflows: AssignmentGroup;
  assignedClients: AssignmentGroup;
  supervisees: AssignmentGroup;
}

export interface DepartResult {
  transferred: { workflows: number; clients: number; supervisees: number };
  departedMemberId: string;
}

// ============================================================================
// New API Methods
// ============================================================================

/**
 * Update a member's role, groups, supervisor, and center
 */
export const updateMember = async (memberId: string, data: UpdateMemberPayload): Promise<void> => {
  await apiClient.patch(`/api/v1/members/${memberId}`, data);
};

/**
 * Deactivate a member
 */
export const deactivateMember = async (memberId: string): Promise<void> => {
  await apiClient.post(`/api/v1/members/${memberId}/deactivate`);
};

export interface ReactivateMemberResult {
  /** True when the member's identity-provider (Entra) sign-in was re-enabled. */
  signInEnabled: boolean;
}

/**
 * Reactivate an inactive member. The API re-enables their identity-provider sign-in
 * BEFORE flipping the membership active; if that fails it responds 502 with
 * `code: SIGN_IN_REENABLE_FAILED` and the member stays inactive (see
 * `isSignInReenableFailure`).
 */
export const reactivateMember = async (memberId: string): Promise<ReactivateMemberResult> => {
  const response = await apiClient.post<{ success: boolean; data?: ReactivateMemberResult }>(
    `/api/v1/members/${memberId}/reactivate`,
  );
  return { signInEnabled: response.data?.data?.signInEnabled ?? false };
};

/** API error `code` when sign-in could not be re-enabled at the identity provider. */
export const SIGN_IN_REENABLE_FAILED = 'SIGN_IN_REENABLE_FAILED';

/**
 * True when a reactivation failed because the member's sign-in could not be re-enabled
 * at the identity provider. The member was NOT reactivated; the UI offers a retry.
 */
export const isSignInReenableFailure = (err: unknown): boolean =>
  (err as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code ===
  SIGN_IN_REENABLE_FAILED;

export interface ResetPasswordResult {
  temporaryPassword: string;
  mustChangePassword: boolean;
}

/**
 * Generate a temporary password for a member and return it once.
 * The member's existing sessions are revoked; they must change the password on next login.
 */
export const resetMemberPassword = async (memberId: string): Promise<ResetPasswordResult> => {
  const response = await apiClient.post<{ success: boolean; data: ResetPasswordResult }>(
    `/api/v1/members/${memberId}/reset-password`,
  );
  return response.data.data;
};

/**
 * Get active assignments for a departing member
 */
export const getActiveAssignments = async (memberId: string): Promise<ActiveAssignments> => {
  const response = await apiClient.get<{ success: boolean; data: ActiveAssignments }>(
    `/api/v1/members/${memberId}/active-assignments`,
  );
  return response.data.data;
};

/**
 * Execute employee departure — transfer assignments and deactivate
 */
export const departMember = async (
  memberId: string,
  data: { transferToMemberId?: string; reason?: string },
): Promise<DepartResult> => {
  const response = await apiClient.post<{ success: boolean; data: DepartResult }>(
    `/api/v1/members/${memberId}/depart`,
    data,
  );
  return response.data.data;
};

// ============================================================================
// Login activation
// ============================================================================

export interface ActivateLoginCredential {
  email: string;
  firstName: string;
  lastName: string;
  temporaryPassword: string;
  // False when the Entra account couldn't be provisioned — the password was still
  // generated/stored, but sign-in won't work until it's retried (e.g. reset-password).
  entraProvisioned: boolean;
}

export interface BulkActivateLoginResult {
  activated: number;
  alreadyActivated: number;
  notFound: number;
  entraProvisioned: number;
  entraFailed: number;
  credentials: ActivateLoginCredential[];
}

/**
 * Activate login for a single member. Sends welcome email and queues Entra registration.
 * Returns the generated temporary password once (surface it via TempPasswordModal).
 * `entraProvisioned: false` means the password was generated but Entra provisioning
 * failed — the credential won't work to sign in until retried.
 */
export const activateLogin = async (
  memberId: string,
): Promise<{ message: string; temporaryPassword?: string; entraProvisioned?: boolean }> => {
  const response = await apiClient.post<{
    success: boolean;
    message: string;
    temporaryPassword?: string;
    entraProvisioned?: boolean;
  }>(`/api/v1/members/${memberId}/activate-login`);
  return {
    message: response.data.message,
    temporaryPassword: response.data.temporaryPassword,
    entraProvisioned: response.data.entraProvisioned,
  };
};

/**
 * Bulk-activate login for many members at once.
 *
 * `organizationId` is only needed for super admins targeting an org other than their
 * JWT context (the backend endpoint reads it from the query string for super admins).
 * Org admins omit it and the JWT org context applies.
 */
export const bulkActivateLogin = async (
  memberIds: string[],
  organizationId?: string,
): Promise<BulkActivateLoginResult> => {
  const response = await apiClient.post<{ success: boolean; data: BulkActivateLoginResult }>(
    '/api/v1/members/bulk-activate-login',
    { memberIds },
    organizationId ? { params: { organizationId } } : undefined,
  );
  return response.data.data;
};

// ============================================================================
// Bulk location assignment
// ============================================================================

export type LocationConflictResolution = 'REPLACE' | 'SKIP';

export interface BulkLocationConflict {
  memberId: string;
  memberName: string;
  currentCenterName: string;
}

export interface BulkLocationPreview {
  totalSelected: number;
  withCenter: BulkLocationConflict[];
  withoutCenterCount: number;
}

export interface BulkLocationResult {
  assigned: number;
  skipped: number;
  transferred: number;
}

export interface BulkLocationPayload {
  memberIds: string[];
  locationId: string;
  conflictResolution?: LocationConflictResolution;
}

/**
 * Preview which selected members already have a primary location (conflict step).
 */
export const previewBulkLocation = async (memberIds: string[]): Promise<BulkLocationPreview> => {
  const response = await apiClient.post<{ success: boolean; data: BulkLocationPreview }>(
    '/api/v1/members/bulk-location/preview',
    { memberIds },
  );
  return response.data.data;
};

/**
 * Bulk-assign a primary location to many members (Transfer/Skip on conflicts).
 */
export const bulkAssignLocation = async (
  payload: BulkLocationPayload,
): Promise<BulkLocationResult> => {
  const response = await apiClient.post<{ success: boolean; data: BulkLocationResult }>(
    '/api/v1/members/bulk-location',
    payload,
  );
  return response.data.data;
};

const memberService = {
  getHierarchy,
  getSeatCount,
  updateSupervisor,
  updateMember,
  deactivateMember,
  reactivateMember,
  resetMemberPassword,
  getActiveAssignments,
  departMember,
  activateLogin,
  bulkActivateLogin,
  previewBulkLocation,
  bulkAssignLocation,
};

export default memberService;
